/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import {
    object, string, func, bool,
} from 'prop-types';
import Cookies from 'universal-cookie';

import loadable from '@loadable/component';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { CheckoutFooter, MbpFooter } from '@footer/mbp-next/app/react';
import { useLocation } from 'react-router';
import AppShell from './AppShell/AppShell';
import CheckoutWrapper from './containers/EnterpriseCheckout/Common/CheckoutWrapper/CheckoutWrapper';
import { trackApp } from '../../helpers/tracking/common/commonTrackingHelpers';
import { loadApp } from '../../../state/ducks/Checkout/ducks/App/App-Actions';
import {
    getFeatureFlag,
    getCheckoutAppConfigLoaded,
    getIsCheckoutReducerLoaded,
} from '../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';

import { getPresentationFamily } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getAppShellLoadedState, getSSRDeviceType } from '../../../state/ducks/App/App-Selectors';
import { getBrand, getOrderId } from '../../../state/ducks/Common/Common-Selectors';
import { getEnterpriseId } from '../../../state/ducks/Member/ducks/EnterpriseId/EnterpriseId-Selectors';
import { getIsAuthenticatedStatus } from '../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import SiteLoader from './containers/EnterpriseCheckout/Common/SiteLoader/SiteLoader';
import { getAnalyticsStorageData } from '../../helpers/tracking/session/sessionTrackingHelpers';
import { loadAppShell } from '../../../state/ducks/App/App-Actions';
import { GRAPHQL_ENV } from '../../gql';

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

const Loader = ({ error }) => {
    if (error) {
        mbpLogger.logError({
            function: 'Loader',
            module: 'mbp-checkout',
            jsError: error,
            message: 'Checkout loadable route failed',
        });
    }
    return <SiteLoader />;
};

Loader.propTypes = {
    error: object,
};

Loader.defaultProps = {
    error: null,
};

const CheckoutAppState = ({ children }) => children;

/**
 * Injectes Checkout State - Reducer / Saga
 * @param {object} store
 * @param {boolean} isCheckoutReducerLoaded
 * @param {function} callLoadApp
 * @returns CheckoutAppState
 */
const loadableCheckoutState = (store, isCheckoutReducerLoaded, callLoadApp) => loadable(() => import(/* webpackChunkName: "CheckoutState" */ '../../../state/ducks/MfeCheckout').then((module) => {
    if (store && !isCheckoutReducerLoaded) {
        // reducer are needed for standalone too, to avoid any undefined state error
        store.injectReducer('mfeCheckout', module.default);

        if (REACT_APP_INJECTABLE !== 'false') {
            // watchers for standalone is already declared in store.js file.
            store.injectSaga('mfeCheckout', module.checkoutSagas);
        }

        callLoadApp();
    }

    return CheckoutAppState;
}), {
    fallback: <Loader />,
});

const LoadableCheckoutEnterprise = loadable(() => import(/* webpackChunkName: "CheckoutEnterprise" */ './CheckoutEnterprise'), {
    fallback: <Loader />,
});

const HeaderBuilder = loadable(() => import(/* webpackChunkName: "HeaderBuilder" */ './containers/Header/HeaderBuilder'));

const getSessionKey = () => {
    const cookies = new Cookies();

    const sessionKey = cookies.get('sessionKey');

    return sessionKey || 'null';
};

const getShopperManagerCookie = () => {
    const cookies = new Cookies();

    return cookies.get('ShopperManagerEnterprise') || 'null';
};

const getBannerCookie = () => {
    const cookies = new Cookies();

    return cookies.get('banner') || 'null';
};

const getWindowLocation = () => {
    if (typeof window !== 'undefined') {
        return window.location.href;
    }

    return 'null';
};

const LogCheckoutFlag = ({
    deviceType,
    ffWhichCheckoutVariation,
    presentationDomainFamily,
}) => {
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const enterpriseId = useSelector(getEnterpriseId);
    const orderId = useSelector(getOrderId) || 'null';

    const pageLocation = getWindowLocation();
    const sessionKey = getSessionKey();
    const shopperManagerCookie = getShopperManagerCookie();
    const bannerCookie = getBannerCookie();
    const analyticsSession = getAnalyticsStorageData();

    useEffect(() => {
        console.log(
            'Checkout Mounted',
            {
                pageLocation,
                isAuthenticated,
                orderId,
                enterpriseId,
                sessionKey,
                deviceType,
                ffWhichCheckoutVariation,
                presentationDomainFamily,
                shopperManagerCookie,
                bannerCookie,
                analyticsSession,
            },
        );
    }, [
        pageLocation,
        isAuthenticated,
        orderId,
        enterpriseId,
        sessionKey,
        deviceType,
        ffWhichCheckoutVariation,
        presentationDomainFamily,
        shopperManagerCookie,
    ]);

    return null;
};

LogCheckoutFlag.propTypes = {
    deviceType: string.isRequired,
    ffWhichCheckoutVariation: string.isRequired,
    presentationDomainFamily: string,
};

LogCheckoutFlag.defaultProps = {
    presentationDomainFamily: 'flowers',
};

const CheckoutFooterComponent = () => {
    const location = useLocation();
    const brand = useSelector(getBrand);
    const clientConfigVars = useSelector(getFeatureFlag('client-config-vars'));
    if (clientConfigVars?.trustArcScript) {
        return (
            <>
                <div className="footer-standalone">
                    {/* footer */}
                    {location?.pathname?.includes?.('/checkout/cart/') && (
                        <div style={{ width: '100%', marginTop: 20 }}>
                            <MbpFooter brand={brand?.domain} environment={GRAPHQL_ENV}  />
                        </div>
                    )}
                    <CheckoutFooter
                        trustArcScript={clientConfigVars?.trustArcScript}
                        brand={brand?.domain}
                        brand2="global"
                        contentType="footer"
                        contentType2="footer_legal_links"
                        includeRefs={['link_group.group_content']}
                        environment={GRAPHQL_ENV}
                    />
                </div>
            </>
        );
    }
    return <></>;
};

const Checkout = ({
    appConfigLoaded,
    callLoadApp,
    deviceType,
    ffWhichCheckoutVariation,
    isCheckoutReducerLoaded,
    isMonitoringCheckoutEnabled,
    presentationDomainFamily,
    store,
    ldClient,
    history,
}) => {
    const dispatch = useDispatch();
    const appShellLoaded = useSelector(getAppShellLoadedState);
    const orderId = useSelector(getOrderId) || 'null';

    useEffect(() => {
        if (REACT_APP_INJECTABLE === 'false') {
            dispatch(loadAppShell({
                hostname: window?.location?.hostname,
                location: history?.location,
                history,
                appShellLoaded,
            }));
        }
    }, []);

    let CheckoutComponent = SiteLoader;
    const CheckoutState = loadableCheckoutState(store, isCheckoutReducerLoaded, callLoadApp);

    /**
     * appShellLoaded need to be true if standalone, but not required for injectable mfe.
     */
    if (isCheckoutReducerLoaded && appConfigLoaded && (appShellLoaded || REACT_APP_INJECTABLE !== 'false')) {
        if (isMonitoringCheckoutEnabled) {
            const cookies = new Cookies();
            mbpLogger.logInfo({
                appName: process.env.npm_package_name,
                module: 'Checkout.js',
                function: 'Checkout',
                errorMessage: (cookies.get('banner')) ? cookies.get('banner') : '',
                message: `Persisting banner cookie ${orderId} - Checkout Start Page`,
            });
        }

        CheckoutComponent = () => (
            <AppShell>
                <HeaderBuilder
                    deviceType={deviceType}
                    ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                    presentationDomainFamily={presentationDomainFamily}
                />
                <CheckoutWrapper
                    ldClient={ldClient}
                >
                    <LoadableCheckoutEnterprise
                        ldClient={ldClient}
                    />
                </CheckoutWrapper>
            </AppShell>
        );

        // Implement Google Recaptcha V3 - Provider
        if (window !== 'undefined'
            && mbpUtil.getEnv('IS_GOOGLE_RECAPTCHA_PROVIDER_ENABLED') === 'true' // Enablement
            && mbpUtil.getEnv('APP_G_RECAPTCHA_CHECKOUT_V3_KEY')
        ) {
            const checkoutReCaptchaKey = mbpUtil.getEnv('APP_G_RECAPTCHA_CHECKOUT_V3_KEY') || '';
            // Rebuild RootComp component for Google Recaptcha
            if (checkoutReCaptchaKey) {
                return (
                    <CheckoutState>
                        <LogCheckoutFlag
                            deviceType={deviceType}
                            ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                            presentationDomainFamily={presentationDomainFamily}
                        />
                        <GoogleReCaptchaProvider
                            reCaptchaKey={checkoutReCaptchaKey}
                            scriptProps={{
                                appendTo: 'head', // optional, default to "head", can be "head" or "body",
                            }}
                        >
                            <CheckoutComponent />
                            {REACT_APP_INJECTABLE === 'false' && <CheckoutFooterComponent />}
                        </GoogleReCaptchaProvider>
                    </CheckoutState>
                );
            }
        }

        return (
            <CheckoutState>
                <LogCheckoutFlag
                    deviceType={deviceType}
                    ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                    presentationDomainFamily={presentationDomainFamily}
                />
                <CheckoutComponent />
                {REACT_APP_INJECTABLE === 'false' && <CheckoutFooterComponent />}
            </CheckoutState>
        );
    }

    return (
        <CheckoutState>
            <LogCheckoutFlag
                deviceType={deviceType}
                ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                presentationDomainFamily={presentationDomainFamily}
            />
            <>
                {ffWhichCheckoutVariation
                    && (
                        <HeaderBuilder
                            deviceType={deviceType}
                            ffWhichCheckoutVariation={ffWhichCheckoutVariation}
                            presentationDomainFamily={presentationDomainFamily}
                        />
                    )}
                <CheckoutComponent />
            </>
        </CheckoutState>
    );
};

const mapStateToProps = (state) => ({
    isCheckoutReducerLoaded: getIsCheckoutReducerLoaded(state),
    deviceType: getSSRDeviceType(state),
    ffWhichCheckoutVariation: getFeatureFlag('which-checkout-variation')(state) || '',
    isPlaceorderBtnRecaptchaEnabled: getFeatureFlag('is-placeorder-btn-captcha-enabled')(state) || false,
    isGiftCardBtnRecaptchaEnabled: getFeatureFlag('is-giftcard-btn-captcha-enabled')(state) || false,
    isMonitoringCheckoutEnabled: getFeatureFlag('is-checkout-monitoring-enabled')(state) || false,
    appConfigLoaded: getCheckoutAppConfigLoaded(state),
    presentationDomainFamily: getPresentationFamily(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    callLoadApp: loadApp,
}, dispatch);

Checkout.propTypes = {
    appConfigLoaded: bool.isRequired,
    callLoadApp: func.isRequired,
    deviceType: string.isRequired,
    ffWhichCheckoutVariation: string.isRequired,
    isCheckoutReducerLoaded: bool.isRequired,
    isMonitoringCheckoutEnabled: bool.isRequired,
    presentationDomainFamily: string,
    store: object.isRequired,
    ldClient: object.isRequired,
    history: object.isRequired,
};

Checkout.defaultProps = {
    presentationDomainFamily: 'flowers',
};

const enhance = compose(
    trackApp('mbp-checkout'),
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(Checkout);
